import { SelectOption } from '../../../../../components/SelectDropdown/SelectDropdown'
import { FormTask, FormValues } from '../AddTaskToCRMModal'
import { Controller, useFormContext } from 'react-hook-form'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { TableColumn } from 'react-data-table-component'
import DatePicker from 'react-datepicker'
import { FormFeedback } from '../../../../../components/Form/FormFeedback'
import { ZocksDataTable } from '../../../../../components/DataTable/ZocksDataTable'
import { useLazyGetIntegrationTeammatesQuery } from '../../../../../store/integrations/integrations'
import { useAppSelector } from '../../../../../store/hooks'
import { selectAccountId } from '../../../../../store/auth/authSlice'
import { Form } from 'react-bootstrap'
import { Button } from '../../../../../components/Button/Button'
import { User } from '../../../../../models/User'
import { taskCategoryEntityType } from './utils'
import { TrackEvent, useTracking } from '../../../../../apps/professional-app/fa/common/tracking'
import Select, {
  components,
  ControlProps,
  CSSObjectWithLabel,
  DropdownIndicatorProps,
  GroupBase,
  MultiValue,
  OptionsOrGroups,
  ValueContainerProps,
} from 'react-select'
import { OptionProps } from 'react-select/dist/declarations/src/components/Option'
import './TaskTable.scss'
import AsyncSelect from 'react-select/async'
import { useDebouncedCallback } from 'use-debounce'

export interface TasksTableProps {
  serviceId: string
  isAllSelected: boolean
  anySelected: boolean
  onSelectAll: (checked: boolean) => void
  tasks: FormTask[]
  taskCategoryOptions: SelectOption[]
  assignedToOptions: SelectOption[]
  onEditTask: (taskId: string) => void
  onDeleteTask: (taskId: string) => void
  editDisabled: boolean
}

export type TaskCategoryEntityTypeConfig = {
  api: string
  dto: string
}

const _STATUS_OPTIONS: SelectOption[] = [
  { id: 'open', label: 'Open' },
  { id: 'close', label: 'Close' },
]

const ReactSelectStyles = {
  menu: (base: CSSObjectWithLabel) => ({
    ...base,
    width: 'max-content',
    minWidth: '0',
    border: '0px solid white',
    boxShadow: '0 4px 20px 0 rgba(0, 0, 0, 0.35)',
  }),
  menuPortal: (base: CSSObjectWithLabel) => ({ ...base, zIndex: 9999999 }),
  option: (base: CSSObjectWithLabel) => ({
    ...base,
    color: '#000',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    ':hover': { backgroundColor: '#f1f1f1' },
  }),
  control: (base: CSSObjectWithLabel, state: ControlProps<SelectOption>) => ({
    ...base,
    borderColor: state.isFocused ? 'var(--zocks-primary)' : 'var(--zocks-gray-500)',
    boxShadow: 'none',
    '&:hover': {
      borderColor: state.isFocused ? 'var(--zocks-primary)' : 'var(--zocks-gray-500)',
    },
  }),
}

export const TasksTable: React.FC<TasksTableProps> = ({
  serviceId,
  isAllSelected,
  anySelected,
  onSelectAll,
  tasks,
  taskCategoryOptions,
  assignedToOptions,
  onEditTask,
  onDeleteTask,
  editDisabled,
}) => {
  const { control } = useFormContext<FormValues>()
  const selectAllCheckboxRef = React.useRef<HTMLInputElement>(null)
  const { tracked } = useTracking()
  useEffect(() => {
    if (selectAllCheckboxRef.current) {
      selectAllCheckboxRef.current.indeterminate = anySelected && !isAllSelected
    }
  }, [anySelected, isAllSelected])

  const priorityOptions = [
    { label: 'Low', id: 'Low' },
    { label: 'Medium', id: 'Medium' },
    { label: 'High', id: 'High' },
  ]

  const handleTaskFieldUpdate = tracked(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-explicit-any
    (field: any, value: unknown, type: string) => {
      field.onChange(value)
    },
    {
      eventName: TrackEvent.TASK_UPDATED,
    },
    {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      eventDetailsExtractor: (field: any, value: unknown, type: string) => {
        return {
          type,
        }
      },
    },
  )

  const columns: TableColumn<FormTask & { id: string }>[] = [
    {
      name: (
        <Form.Check
          ref={selectAllCheckboxRef}
          id='select-all-tasks-header-checkbox'
          checked={isAllSelected}
          onChange={() => onSelectAll(!anySelected)}
          title='Select all'
        />
      ),
      width: '3rem',
      button: true,
      ignoreRowClick: true,
      selector: (row) => row.selected,
      cell: (row, rowIndex) => (
        <Controller
          name={`tasks.${rowIndex}.selected`}
          control={control}
          render={({ field }) => <Form.Check id={`select-task-checkbox-${row.id}`} checked={field.value} onChange={field.onChange} />}
        />
      ),
    },
    {
      name: 'Task',
      wrap: true,
      grow: 2,
      cell: (row) => (
        <span>
          <strong>
            {row.task}
            {row.description ? ':' : ''}
          </strong>{' '}
          {row.description}
        </span>
      ),
    },
    {
      name: 'Due date',
      wrap: true,
      width: '16%',
      cell: (_, rowIndex) => (
        <Controller
          name={`tasks.${rowIndex}.dueDate`}
          control={control}
          rules={{ required: 'Due date is required.' }}
          render={({ field, fieldState }) => (
            <div className={'task-date-row'}>
              <label htmlFor={`tasks-date-${rowIndex}`} className={'d-flex align-items-center '}>
                <i className={'bi bi-calendar ps-3'} />
                <DatePicker
                  id={`tasks-date-${rowIndex}`}
                  className='form-control border-0'
                  selected={field.value}
                  onChange={(date) => handleTaskFieldUpdate(field, date, 'dueDateChanged')}
                  todayButton='Today'
                  popperProps={{
                    strategy: 'fixed',
                  }}
                />
              </label>
              {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
            </div>
          )}
        />
      ),
    },
    {
      name: 'Assigned To',
      wrap: true,
      width: '15%',
      cell: (_, rowIndex) => (
        <Controller
          name={serviceId === 'redtail' ? `tasks.${rowIndex}.multipleAssignedTo` : `tasks.${rowIndex}.assignedTo`}
          control={control}
          rules={{ required: 'Assigned to is required.' }}
          render={({ field, fieldState }) => {
            return (
              <div className='d-flex flex-1 flex-column'>
                <AssignedToDropdown
                  serviceId={serviceId}
                  selectedAssignedToIds={field.value ?? []}
                  onOptionSelected={(assignedTo) => handleTaskFieldUpdate(field, assignedTo, 'assignedToChanged')}
                  initialOptions={assignedToOptions}
                  dropdownName={field.name}
                />
                {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
              </div>
            )
          }}
        />
      ),
    },
    {
      name: 'Priority',
      width: '15%',
      wrap: true,
      cell: (_, rowIndex) => (
        <Controller
          name={`tasks.${rowIndex}.priority`}
          control={control}
          render={({ field }) => (
            <div className='w-100'>
              <Select<SelectOption, false>
                isSearchable={false}
                options={priorityOptions}
                value={priorityOptions.find((option) => option.id === field.value)}
                onChange={(option) => handleTaskFieldUpdate(field, option?.id, 'priorityChanged')}
                menuPortalTarget={document.body}
                placeholder={'Select your priority'}
                getOptionValue={(option) => option.id}
                getOptionLabel={(option) => option.label}
                hideSelectedOptions={false}
                styles={ReactSelectStyles}
                components={{
                  DropdownIndicator: CustomDropdownIndicator,
                }}
              />
            </div>
          )}
        />
      ),
    },
    {
      name: 'Category',
      width: '15%',
      wrap: true,
      omit: !((Object.keys(taskCategoryEntityType).includes(serviceId) && taskCategoryOptions.length > 1) || serviceId === 'smartoffice'),
      cell: (_, rowIndex) => (
        <Controller
          name={`tasks.${rowIndex}.category`}
          control={control}
          render={({ field }) => (
            <div className='w-100'>
              <CategoryColumnDropdown
                selectedTaskCategoryId={field.value}
                taskCategoryOptions={taskCategoryOptions}
                onOptionSelected={(category) => handleTaskFieldUpdate(field, category, 'categoryChanged')}
              />
            </div>
          )}
        />
      ),
    },
    {
      name: 'Status',
      width: '11%',
      cell: (_, rowIndex) => (
        <Controller
          name={`tasks.${rowIndex}.status`}
          control={control}
          render={({ field }) => (
            <div className='w-100'>
              <StatusColumnDropdown selectedTaskStatusId={field.value} taskStatusOptions={_STATUS_OPTIONS} onOptionSelected={field.onChange} />
            </div>
          )}
        />
      ),
    },
    {
      name: '',
      width: '5%',
      right: true,
      cell: (row) => (
        <div className='d-flex justify-content-center p-0'>
          <Button onClick={() => onDeleteTask(row.id)} className='delete-task-button p-0' aria-label='Delete Task'>
            <i className='bi bi-trash' />
          </Button>
          {!editDisabled ? (
            <Button onClick={() => onEditTask(row.id)} className='edit-task-button p-0 ps-3' aria-label='Edit Task'>
              <i className='bi bi-pencil-square' />
            </Button>
          ) : null}
        </div>
      ),
    },
  ]

  return (
    <div className='add-to-crm-modal-tasks-table'>
      <ZocksDataTable columns={columns} data={tasks} fixedHeader fixedHeaderScrollHeight='100%' />
    </div>
  )
}

interface CategoryColumnDropdownProps {
  selectedTaskCategoryId: string | null
  taskCategoryOptions: SelectOption[]
  onOptionSelected: (optionId: string | null) => void
}

const CategoryColumnDropdown: React.FC<CategoryColumnDropdownProps> = ({ selectedTaskCategoryId, taskCategoryOptions, onOptionSelected }) => {
  const [selectedCategory, setSelectedCategory] = useState<SelectOption | null>(() =>
    selectedTaskCategoryId ? (taskCategoryOptions.find((o) => o.id === selectedTaskCategoryId) ?? null) : null,
  )

  return (
    <Select<SelectOption, false>
      options={taskCategoryOptions}
      value={selectedCategory}
      onChange={(option) => {
        if (!option) {
          return
        }
        setSelectedCategory(option.id === null ? null : option)
        onOptionSelected(option.id)
      }}
      menuPortalTarget={document.body}
      placeholder={'Select…'}
      getOptionValue={(option) => option.id}
      getOptionLabel={(option) => option.label}
      hideSelectedOptions={false}
      styles={ReactSelectStyles}
      components={{
        DropdownIndicator: CustomDropdownIndicator,
      }}
    />
  )
}

interface StatusColumnDropdownProps {
  selectedTaskStatusId: string | null
  taskStatusOptions: SelectOption[]
  onOptionSelected: (optionId: string | null) => void
}

const StatusColumnDropdown: React.FC<StatusColumnDropdownProps> = ({ selectedTaskStatusId, taskStatusOptions, onOptionSelected }) => {
  const [selectedStatus, setSelectedStatus] = useState<SelectOption | null>(() =>
    selectedTaskStatusId ? (taskStatusOptions.find((o) => o.id === selectedTaskStatusId) ?? null) : null,
  )

  return (
    <Select<SelectOption, false>
      isSearchable={false}
      options={taskStatusOptions}
      value={selectedStatus}
      onChange={(option) => {
        if (!option) {
          return
        }
        setSelectedStatus(option.id === null ? null : option)
        onOptionSelected(option.id)
      }}
      menuPortalTarget={document.body}
      placeholder={'Select…'}
      getOptionValue={(option) => option.id}
      getOptionLabel={(option) => option.label}
      hideSelectedOptions={false}
      styles={ReactSelectStyles}
      components={{
        DropdownIndicator: CustomDropdownIndicator,
      }}
    />
  )
}

interface AssignedToDropdownProps {
  serviceId: string
  selectedAssignedToIds: string | string[] | null
  onOptionSelected: (optionId: string[] | string | null) => void
  initialOptions: SelectOption[]
  dropdownName: string
}

const AssignedToDropdown: React.FC<AssignedToDropdownProps> = ({ serviceId, selectedAssignedToIds, onOptionSelected, initialOptions, dropdownName }) => {
  const enableBackendSearchServiceIds = ['salesforce', 'practifi', 'xlr8', 'smartoffice', 'elements', 'blackdiamond']
  const accountId = useAppSelector(selectAccountId)
  const [triggerGetIntegrationTeammatesQuery] = useLazyGetIntegrationTeammatesQuery()
  const [availableOptions, setAvailableOptions] = useState<SelectOption[]>([...initialOptions])

  const getAssignedToOptions = useCallback(
    (users: User[]) => {
      return users.map((user) => {
        const contactIntegration = user.integrations?.find((i) => i.serviceId === serviceId)
        const contactLabel = `${user.name ? user.name : ''}${user.email ? ` (${user.email})` : ''}`
        return { id: contactIntegration!.externalId, label: contactLabel }
      })
    },
    [serviceId],
  )

  const handleBackendSearchInputChange = useCallback(
    (searchInput: string, callback: (options: OptionsOrGroups<SelectOption, GroupBase<SelectOption>>) => void) => {
      try {
        triggerGetIntegrationTeammatesQuery({
          accountId,
          connector: serviceId,
          filters: { name: searchInput },
        })
          .unwrap()
          .then((response) => {
            if (response && response.data) {
              const options = getAssignedToOptions(response.data)
              setAvailableOptions(options)
              callback(options)
            }
          })
      } catch (e) {
        console.error('Failed to search teammates:', e)
      }
    },
    [accountId, serviceId, triggerGetIntegrationTeammatesQuery, getAssignedToOptions],
  )

  const debouncedLoader = useDebouncedCallback(handleBackendSearchInputChange, 400)

  const selectedOption = useMemo(
    () => availableOptions.find((option) => option.id === selectedAssignedToIds) ?? null,
    [availableOptions, selectedAssignedToIds],
  )

  if (serviceId === 'redtail') {
    const currentSelectedIds = Array.isArray(selectedAssignedToIds) ? selectedAssignedToIds : selectedAssignedToIds ? [selectedAssignedToIds] : []

    return (
      <MultiSelectAssignedToDropdown
        serviceId={serviceId}
        selectedAssignedToIds={currentSelectedIds}
        onOptionSelected={onOptionSelected}
        initialOptions={initialOptions}
        dropdownName={dropdownName}
      />
    )
  }

  return (
    <>
      {enableBackendSearchServiceIds.includes(serviceId) ? (
        <AsyncSelect<SelectOption, false>
          cacheOptions
          defaultOptions={availableOptions}
          value={selectedOption}
          onChange={(option) => {
            if (!option) {
              return
            }
            onOptionSelected(option.id)
          }}
          loadOptions={debouncedLoader}
          menuPortalTarget={document.body}
          placeholder={'Select assignee…'}
          getOptionValue={(option) => option.id}
          getOptionLabel={(option) => option.label}
          hideSelectedOptions={false}
          styles={ReactSelectStyles}
        />
      ) : (
        <Select<SelectOption, false>
          options={initialOptions}
          value={selectedOption}
          onChange={(option) => {
            if (!option) {
              return
            }
            onOptionSelected(option.id)
          }}
          menuPortalTarget={document.body}
          placeholder={'Select assignee…'}
          getOptionValue={(option) => option.id}
          getOptionLabel={(option) => option.label}
          hideSelectedOptions={false}
          styles={ReactSelectStyles}
          components={{
            DropdownIndicator: CustomDropdownIndicator,
          }}
        />
      )}
    </>
  )
}

const MultiSelectAssignedToDropdown: React.FC<AssignedToDropdownProps> = ({ selectedAssignedToIds, onOptionSelected, initialOptions }) => {
  const selectedOptions = useMemo(() => {
    return initialOptions.filter((o) => selectedAssignedToIds?.includes(o.id))
  }, [selectedAssignedToIds, initialOptions])

  const handleOnChange = (options: MultiValue<SelectOption>) => {
    onOptionSelected(options.map((o) => o.id))
  }

  return (
    <Select<SelectOption, true>
      options={initialOptions}
      value={selectedOptions}
      onChange={handleOnChange}
      isMulti
      menuPortalTarget={document.body}
      placeholder={'Select assignee'}
      getOptionValue={(option) => option.id}
      getOptionLabel={(option) => option.label}
      components={{
        Option: CustomOption,
        ValueContainer: CustomValueContainer,
        DropdownIndicator: CustomDropdownIndicator,
        MultiValue: () => null,
      }}
      closeMenuOnSelect={false}
      hideSelectedOptions={false}
      styles={ReactSelectStyles}
    />
  )
}

const CustomDropdownIndicator = (props: DropdownIndicatorProps<SelectOption>) => {
  const { menuIsOpen } = props.selectProps

  return (
    <components.DropdownIndicator {...props}>
      {menuIsOpen ? <i className='bi bi-chevron-contract' /> : <i className='bi bi-chevron-expand' />}
    </components.DropdownIndicator>
  )
}

const CustomValueContainer = (props: ValueContainerProps<SelectOption>) => {
  const selected = (props.selectProps.value || []) as MultiValue<SelectOption>
  return (
    <components.ValueContainer {...props}>
      <div className='d-flex align-items-center'>
        {selected.length && !props.selectProps.inputValue.length
          ? `${selected.length > 1 ? `${selected.length} assignees selected` : selected[0].label}`
          : null}{' '}
        {props.children}
      </div>
    </components.ValueContainer>
  )
}
const CustomOption = (option: OptionProps<SelectOption>) => {
  const handleOnClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    option.selectOption(option.data)
  }
  return (
    <components.Option {...option}>
      <div onClick={handleOnClick} className='multi-select-option'>
        <Form.Check
          className='multi-select-option'
          key={option.data.id}
          type='checkbox'
          id={`option_${option.data.id}`}
          label={option.label}
          onChange={() => null}
          checked={option.isSelected}
          value={option.data.id}
        />
      </div>
    </components.Option>
  )
}
